import { useState, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

export const useAvailability = () => {
  const [horasDisponibles, setHorasDisponibles] = useState([]);
  const [horasOcupadas, setHorasOcupadas] = useState([]);
  const [horasCargadas, setHorasCargadas] = useState(false);

  // Cargar horas disponibles para una fecha y servicio
  const cargarHorasDisponibles = useCallback(async (fecha, servicioId, citas, servicios) => {
    try {
      setHorasCargadas(false);

      const citasDelDia = citas.filter(c => c.estado !== 'cancelada');

      const citasRes = await api.get(`/citas/disponibilidad/${fecha}`);
      const respuesta = citasRes.data;

      // Procesar horas ocupadas basado en citas existentes
      const ocupadas = [];
      citasDelDia.forEach(cita => {
        const servicioCita = servicios.find(s => s.id == cita.servicio_id);
        const duracion = servicioCita?.duracion || 60;

        const rangosBloqueados = citasDelDia.map(c => {
          const servC = servicios.find(s => s.id == c.servicio_id);
          const dur = servC?.duracion || 60;
          return {
            inicio: new Date(`${c.fecha_cita}T${c.hora_inicio}`),
            fin: new Date(`${c.fecha_cita}T${c.hora_inicio}`) + dur * 60 * 1000
          };
        });

        const horaInicio = new Date(`${cita.fecha_cita}T${cita.hora_inicio}`);
        const horaFin = new Date(horaInicio.getTime() + duracion * 60 * 1000);

        ocupadas.push({
          inicio: cita.hora_inicio,
          fin: formatTime(horaFin)
        });
      });

      setHorasOcupadas(ocupadas);

      // Si se especifica servicioId, filtrar disponibilidad por ese servicio
      const disponibles = respuesta.disponibles || [];
      if (servicioId) {
        const servicio = servicios.find(s => s.id == servicioId);
        const duracion = servicio?.duracion || 60;

        const filtradas = disponibles.filter(hora => {
          const horaActual = new Date(`${fecha}T${hora}`);
          const horaFinal = new Date(horaActual.getTime() + duracion * 60 * 1000);

          return !ocupadas.some(rango => {
            const rangoInicio = new Date(`${fecha}T${rango.inicio}`);
            const rangoFin = new Date(`${fecha}T${rango.fin}`);
            return horaActual < rangoFin && horaFinal > rangoInicio;
          });
        });

        setHorasDisponibles(filtradas);
      } else {
        setHorasDisponibles(disponibles);
      }

      setHorasCargadas(true);
    } catch (error) {
      toast.error('Error al cargar horas disponibles');
      setHorasDisponibles([]);
      setHorasCargadas(true);
    }
  }, []);

  // Helper para formatear hora
  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Verificar si una hora está disponible
  const isHoraDisponible = useCallback((hora) => {
    return horasDisponibles.includes(hora);
  }, [horasDisponibles]);

  // Verificar si una hora está ocupada
  const isHoraOcupada = useCallback((hora) => {
    return horasOcupadas.some(rango => {
      const [h1, m1] = hora.split(':').map(Number);
      const [h2, m2] = rango.inicio.split(':').map(Number);
      return h1 === h2 && m1 === m2;
    });
  }, [horasOcupadas]);

  // Horas para crear/reagendar citas: todo el horario de atención del día (sin límite
  // por cita existente: varios clientes pueden agendarse a la misma hora); solo se
  // ocultan las horas ya pasadas de hoy.
  const cargarHoras = useCallback(async (fecha) => {
    setHorasCargadas(false);
    if (!fecha) {
      setHorasDisponibles([]);
      return;
    }
    try {
      const { data } = await api.get('/citas/horas-disponibles', { params: { fecha } });
      const ahora = new Date();
      const hoy = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(ahora.getDate()).padStart(2, '0')}`;
      const horaActual = ahora.toTimeString().substring(0, 5);
      setHorasDisponibles(fecha === hoy ? (data || []).filter(h => h > horaActual) : (data || []));
    } catch {
      setHorasDisponibles([]);
    } finally {
      setHorasCargadas(true);
    }
  }, []);

  return {
    cargarHoras,
    horasDisponibles,
    horasOcupadas,
    horasCargadas,
    setHorasDisponibles,
    setHorasOcupadas,
    setHorasCargadas,
    cargarHorasDisponibles,
    isHoraDisponible,
    isHoraOcupada
  };
};
